// AbPdfViewer — wiederverwendbarer PDF-Arbeitsplatz (pdfjs-Canvas + Wort-Box-Overlay + Zoom +
// Fundstellen-Toggle). WIEDERVERWENDBAR: Welle 2 (Feld-Mapping-Editor) nutzt dieselbe Komponente.
// Import-Muster verifiziert gruen im CRA-Build (Bauplan-Vorarbeit):
//   import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
//   pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;
//
// Laedt das PDF SELBST als Blob-URL (api.pdfBlobUrl, JWT-Bearer-Header) und rendert je Seite ein
// <canvas> (pdfjs) mit absolut positioniertem Overlay fuer (a) klickbare Wort-Boxen (Klick-
// Uebernahme-Modus, nur wenn onWordClick gesetzt ist) und (b) Fundstellen-Markierungen
// (highlights-Prop, z. B. aus wordMatch.buildHighlights). Seiten werden lazy gerendert
// (IntersectionObserver) und bei Zoom-Aenderung fuer bereits sichtbare Seiten neu gerastert.
//
// Koordinatensystem: OCR-Wort-Boxen (words) UND highlights-Boxen liegen in PDF-Punkt-Koordinaten
// mit Ursprung oben links (wie die Seiten-Metadaten aus GET /documents/:id/words) — die
// Canvas-Pixelposition ergibt sich direkt aus Koordinate * scale (pdfjs-Viewport-Skalierung).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';
import { Minus, Plus, RotateCcw, Eye, EyeOff, Loader2, AlertTriangle, FileX } from 'lucide-react';
import { pdfBlobUrl } from '../../api';

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;

const MIN_SCALE = 0.5;
const MAX_SCALE = 3;
const DEFAULT_SCALE = 1.15;
const SCALE_STEP = 0.15;

const toolBtnCls = (isDarkMode, disabled) => `inline-flex items-center justify-center w-8 h-8 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${isDarkMode
  ? 'text-white/70 hover:bg-white/10' : 'text-slate-600 hover:bg-slate-100'} ${disabled ? '' : 'cursor-pointer'}`;

const AbPdfViewer = ({
  user, documentId, words = null, highlights = [], activeHighlightId = null,
  onWordClick = null, clickMode = false, isDarkMode, t, heightPx = 700,
}) => {
  const [scale, setScale] = useState(DEFAULT_SCALE);
  const [showFindings, setShowFindings] = useState(true);
  const [status, setStatus] = useState('idle'); // idle | loading | ready | error
  const [errorMsg, setErrorMsg] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [pageDims, setPageDims] = useState({}); // { [page]: { width, height } } bei scale=1 (PDF-Punkte)
  const [renderedPages, setRenderedPages] = useState(() => new Set());
  const [reloadToken, setReloadToken] = useState(0);

  const pdfDocRef = useRef(null);
  const pagesRef = useRef({});
  const containerRefs = useRef({});
  const canvasRefs = useRef({});
  const renderedScaleRef = useRef({});
  const blobUrlRef = useRef(null);
  const scrollRef = useRef(null);
  const scaleRef = useRef(scale);
  useEffect(() => { scaleRef.current = scale; }, [scale]);

  const hasHighlights = highlights.length > 0;

  // ---- PDF laden (Blob-URL via api.pdfBlobUrl) — neu bei documentId- oder reloadToken-Wechsel ----
  useEffect(() => {
    if (!documentId) { setStatus('idle'); return undefined; }
    let cancelled = false;
    setStatus('loading');
    setErrorMsg(null);
    setNumPages(0);
    setPageDims({});
    setRenderedPages(new Set());
    renderedScaleRef.current = {};
    pagesRef.current = {};
    pdfDocRef.current = null;
    setScale(DEFAULT_SCALE);
    setShowFindings(true);
    (async () => {
      try {
        const url = await pdfBlobUrl(user, documentId);
        if (cancelled) { URL.revokeObjectURL(url); return; }
        if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = url;
        const pdf = await pdfjsLib.getDocument(url).promise;
        if (cancelled) return;
        pdfDocRef.current = pdf;
        setNumPages(pdf.numPages);
        setStatus('ready');
      } catch (e) {
        if (!cancelled) { setErrorMsg(e?.message || String(e)); setStatus('error'); }
      }
    })();
    return () => {
      cancelled = true;
      if (blobUrlRef.current) { URL.revokeObjectURL(blobUrlRef.current); blobUrlRef.current = null; }
      pdfDocRef.current = null;
    };
  }, [user, documentId, reloadToken]);

  // ---- Seiten-Metadaten (Groesse bei scale=1) fuer alle Seiten laden — NUR Metadaten, kein Rasterisieren. ----
  useEffect(() => {
    if (status !== 'ready' || !numPages) return undefined;
    let cancelled = false;
    (async () => {
      const pdf = pdfDocRef.current;
      if (!pdf) return;
      const dims = {};
      for (let p = 1; p <= pdf.numPages; p += 1) {
        if (cancelled) return;
        try {
          const page = pagesRef.current[p] || await pdf.getPage(p);
          pagesRef.current[p] = page;
          const vp = page.getViewport({ scale: 1 });
          dims[p] = { width: vp.width, height: vp.height };
        } catch { /* einzelne Seite nicht ladbar -> ueberspringen, Rest bleibt nutzbar */ }
      }
      if (!cancelled) setPageDims(dims);
    })();
    return () => { cancelled = true; };
  }, [status, numPages]);

  // ---- Eine Seite bei einer bestimmten Zoomstufe rastern (Canvas neu zeichnen). ----
  const renderPage = useCallback(async (pageNo, targetScale) => {
    if (renderedScaleRef.current[pageNo] === targetScale) return;
    const pdf = pdfDocRef.current;
    const canvas = canvasRefs.current[pageNo];
    if (!pdf || !canvas) return;
    try {
      let page = pagesRef.current[pageNo];
      if (!page) { page = await pdf.getPage(pageNo); pagesRef.current[pageNo] = page; }
      const viewport = page.getViewport({ scale: targetScale });
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      const ctx = canvas.getContext('2d');
      await page.render({ canvasContext: ctx, viewport }).promise;
      renderedScaleRef.current[pageNo] = targetScale;
      setRenderedPages((prev) => (prev.has(pageNo) ? prev : new Set(prev).add(pageNo)));
    } catch {
      /* Seite konnte nicht gerastert werden (z. B. Wechsel waehrenddessen) -> Platzhalter bleibt leer */
    }
  }, []);

  // ---- Erste Seite sofort rendern (sobald ihre Groesse bekannt ist), ohne auf Scroll zu warten. ----
  useEffect(() => {
    if (status !== 'ready' || !pageDims[1]) return;
    renderPage(1, scale);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, pageDims, renderPage]);

  // ---- Lazy-Rendering weiterer Seiten via IntersectionObserver. ----
  useEffect(() => {
    if (status !== 'ready' || !numPages) return undefined;
    const io = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const p = Number(entry.target.getAttribute('data-page'));
        if (p) renderPage(p, scaleRef.current);
      }
    }, { root: scrollRef.current, rootMargin: '300px 0px', threshold: 0.01 });
    Object.values(containerRefs.current).forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, [status, numPages, renderPage]);

  // ---- Zoom-Aenderung: bereits gerenderte Seiten neu rastern (scharf statt CSS-skaliert). ----
  useEffect(() => {
    if (status !== 'ready') return;
    for (const p of renderedPages) renderPage(p, scale);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scale]);

  // ---- Aktive Fundstelle: fehlende Seite nachladen + sanft in den sichtbaren Bereich scrollen. ----
  useEffect(() => {
    if (!activeHighlightId || status !== 'ready') return;
    const match = highlights.find((h) => h.id === activeHighlightId);
    if (!match) return;
    if (renderedScaleRef.current[match.page] !== scale) renderPage(match.page, scale);
    const el = scrollRef.current?.querySelector('[data-active-highlight="1"]');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
  }, [activeHighlightId, highlights, status, scale, renderPage]);

  const wordsByPage = useMemo(() => {
    const m = new Map();
    for (const pg of words?.pages || []) m.set(pg.page, pg.words || []);
    return m;
  }, [words]);

  const highlightsByPage = useMemo(() => {
    const m = new Map();
    for (const h of highlights) {
      if (!h || h.page == null) continue;
      const arr = m.get(h.page) || [];
      arr.push(h);
      m.set(h.page, arr);
    }
    return m;
  }, [highlights]);

  const zoomOut = () => setScale((s) => Math.max(MIN_SCALE, +(s - SCALE_STEP).toFixed(2)));
  const zoomIn = () => setScale((s) => Math.min(MAX_SCALE, +(s + SCALE_STEP).toFixed(2)));
  const zoomReset = () => setScale(DEFAULT_SCALE);
  const retry = () => setReloadToken((v) => v + 1);

  const border = isDarkMode ? 'border-white/10' : 'border-slate-200';
  const textMuted = isDarkMode ? 'text-white/50' : 'text-slate-500';

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5 flex-wrap">
        <button type="button" onClick={zoomOut} disabled={scale <= MIN_SCALE} title={t('abPdf.zoomOut')}
          className={toolBtnCls(isDarkMode, scale <= MIN_SCALE)}>
          <Minus className="w-4 h-4" />
        </button>
        <span className={`text-xs tabular-nums w-11 text-center ${textMuted}`}>{Math.round(scale * 100)}%</span>
        <button type="button" onClick={zoomIn} disabled={scale >= MAX_SCALE} title={t('abPdf.zoomIn')}
          className={toolBtnCls(isDarkMode, scale >= MAX_SCALE)}>
          <Plus className="w-4 h-4" />
        </button>
        <button type="button" onClick={zoomReset} title={t('abPdf.zoomReset')} className={toolBtnCls(isDarkMode, false)}>
          <RotateCcw className="w-4 h-4" />
        </button>
        {hasHighlights && (
          <button type="button" onClick={() => setShowFindings((s) => !s)} aria-pressed={showFindings}
            title={t('abPdf.findingsToggle')}
            className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-lg transition-colors ${showFindings
              ? (isDarkMode ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-100 text-indigo-700')
              : (isDarkMode ? 'bg-white/10 text-white/50' : 'bg-slate-100 text-slate-500')}`}>
            {showFindings ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
            {t('abPdf.findingsToggle')}
          </button>
        )}
        <div className="flex-1" />
        {status === 'loading' && (
          <span className={`text-xs flex items-center gap-1.5 ${textMuted}`}>
            <Loader2 className="w-3.5 h-3.5 animate-spin" />{t('abPdf.pdfLoading')}
          </span>
        )}
      </div>

      {showFindings && hasHighlights && (
        <p className={`text-xs ${textMuted}`}>{t('abPdf.findingsHint')}</p>
      )}

      <div ref={scrollRef} className={`relative overflow-auto rounded-xl border ${border} ${isDarkMode ? 'bg-black/20' : 'bg-slate-100'}`} style={{ height: heightPx }}>
        {!documentId && (
          <div className={`flex flex-col items-center justify-center h-full gap-2 ${textMuted}`}>
            <FileX className="w-8 h-8" />
            <p className="text-sm">{t('abPdf.pdfEmpty')}</p>
          </div>
        )}

        {documentId && status === 'error' && (
          <div className={`flex flex-col items-center justify-center h-full gap-2 px-4 text-center ${isDarkMode ? 'text-red-300' : 'text-red-600'}`}>
            <AlertTriangle className="w-8 h-8" />
            <p className="text-sm">{t('abPdf.pdfLoadError')}{errorMsg ? `: ${errorMsg}` : ''}</p>
            <button type="button" onClick={retry}
              className={`text-xs font-medium px-3 py-1.5 rounded-lg ${isDarkMode ? 'bg-white/10 hover:bg-white/15 text-white/80' : 'bg-slate-200 hover:bg-slate-300 text-slate-700'}`}>
              {t('abPdf.pdfRetry')}
            </button>
          </div>
        )}

        {documentId && status === 'loading' && numPages === 0 && (
          <div className={`flex items-center justify-center h-full gap-2 ${textMuted}`}>
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        )}

        {documentId && (status === 'ready' || (status === 'loading' && numPages > 0)) && (
          <div className="p-3 flex flex-col items-center gap-3">
            {Array.from({ length: numPages }).map((_, i) => {
              const p = i + 1;
              const dims = pageDims[p];
              const w = dims ? dims.width * scale : 320;
              const h = dims ? dims.height * scale : 440;
              const pageWords = clickMode ? (wordsByPage.get(p) || []) : [];
              const found = (showFindings ? (highlightsByPage.get(p) || []) : []).filter((h2) => h2.id !== activeHighlightId);
              const active = (highlightsByPage.get(p) || []).filter((h2) => h2.id === activeHighlightId);
              return (
                <div key={p} data-page={p} ref={(el) => { containerRefs.current[p] = el; }}
                  className={`relative shadow-md flex-shrink-0 ${isDarkMode ? 'bg-white' : 'bg-white'}`}
                  style={{ width: w, height: h }}>
                  <canvas ref={(el) => { canvasRefs.current[p] = el; }} className="block" style={{ width: w, height: h }} />

                  {renderedPages.has(p) && pageWords.map((w2, idx) => (
                    <div key={idx} title={w2.t}
                      className="absolute cursor-pointer hover:bg-blue-400/25 hover:ring-1 hover:ring-blue-400/60 rounded-sm"
                      style={{ left: w2.x0 * scale, top: w2.y0 * scale, width: Math.max(2, (w2.x1 - w2.x0) * scale), height: Math.max(2, (w2.y1 - w2.y0) * scale) }}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={(e) => onWordClick && onWordClick({ t: w2.t, x0: w2.x0, y0: w2.y0, x1: w2.x1, y1: w2.y1, page: p }, e)}
                    />
                  ))}

                  {found.flatMap((h2, hi) => h2.boxes.map((b, bi) => (
                    <div key={`f-${hi}-${bi}`}
                      className="absolute pointer-events-none rounded-sm ring-1 ring-indigo-400/70 bg-indigo-400/10"
                      style={{ left: b.x0 * scale - 2, top: b.y0 * scale - 1, width: (b.x1 - b.x0) * scale + 4, height: (b.y1 - b.y0) * scale + 2 }} />
                  )))}

                  {active.flatMap((h2, hi) => h2.boxes.map((b, bi) => (
                    <div key={`a-${hi}-${bi}`} data-active-highlight="1"
                      className="absolute pointer-events-none rounded-sm ring-2 ring-amber-500 bg-amber-400/25"
                      style={{ left: b.x0 * scale - 3, top: b.y0 * scale - 2, width: (b.x1 - b.x0) * scale + 6, height: (b.y1 - b.y0) * scale + 4 }} />
                  )))}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AbPdfViewer;
